import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  deleteAbsenceType,
  getAbsenceTypeEmployees,
  toggleAbsenceTypeActive,
  unassignEmployeeFromAbsenceType,
  type AbsenceType,
} from '@/lib/absences';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Loader2, Settings, Trash2, Users, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface AbsenceTypeCardProps {
  absenceType: AbsenceType;
  onConfigure?: (absenceType: AbsenceType) => void;
}

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

export function AbsenceTypeCard({ absenceType, onConfigure }: AbsenceTypeCardProps) {
  const queryClient = useQueryClient();
  const formatName = useEmployeeNameFormatter();
  const [isHovered, setIsHovered] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeesDialogOpen, setEmployeesDialogOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => deleteAbsenceType(absenceType.id),
    onSuccess: () => {
      // Invalidate all related queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
      queryClient.invalidateQueries({ queryKey: ['absence-policies'] });
      queryClient.invalidateQueries({ queryKey: ['absence-balances'] });
      setDeleteDialogOpen(false);
    },
    onError: (err) => {
      // Deliberately doesn't touch deleteDialogOpen — keeps the dialog open so the user can retry.
      toast.error(extractErrorMessage(err, 'Failed to delete absence type'));
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: () => toggleAbsenceTypeActive(absenceType.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
      queryClient.invalidateQueries({ queryKey: ['employee-policies'] });
    },
  });

  const {
    data: employees,
    isLoading: isLoadingEmployees,
    refetch: refetchEmployees,
  } = useQuery({
    queryKey: ['absence-types', absenceType.id, 'employees'],
    queryFn: () => getAbsenceTypeEmployees(absenceType.id),
    enabled: employeesDialogOpen,
  });

  const unassignMutation = useMutation({
    mutationFn: (employeeId: string) => unassignEmployeeFromAbsenceType(absenceType.id, employeeId),
    onSuccess: (_, employeeId) => {
      refetchEmployees();
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
      // Invalidate specific employee's policies cache so profile shows updated state
      queryClient.invalidateQueries({ queryKey: ['employee-policies', employeeId] });
    },
  });

  const isActive = absenceType.isActive ?? true;
  const hasAssignedEmployees =
    absenceType._count?.assignments != null && absenceType._count.assignments > 0;

  return (
    <>
      <Card
        className={cn(
          'transition-all duration-200',
          isHovered && 'shadow-md',
          !isActive && 'opacity-60'
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CardContent className="flex items-center gap-4 py-4">
          {/* Active toggle - subtle */}
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center">
                  <Switch
                    checked={isActive}
                    onCheckedChange={() => toggleActiveMutation.mutate()}
                    disabled={toggleActiveMutation.isPending}
                    className="scale-75"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {isActive ? 'Active - visible to employees' : 'Inactive - hidden from employees'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Color indicator */}
          <div
            className="w-4 h-4 rounded-full shrink-0"
            style={{ backgroundColor: absenceType.color || '#9E9E9E' }}
          />

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3
              className={cn('font-medium text-sm truncate', !isActive && 'text-muted-foreground')}
            >
              {absenceType.name}
            </h3>
            <p className="text-xs text-muted-foreground">
              {absenceType.isPaid ? 'Paid' : 'Unpaid'}
              {absenceType.defaultDays > 0 && ` • ${absenceType.defaultDays} days default`}
            </p>
          </div>

          {/* Actions - visible on hover */}
          <div
            className={cn(
              'flex items-center gap-1 transition-opacity duration-200',
              isHovered ? 'opacity-100' : 'opacity-0'
            )}
          >
<TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 cursor-pointer rounded-full text-muted-foreground hover:text-foreground"
                    onClick={() => setEmployeesDialogOpen(true)}
                    aria-label="View assigned employees"
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>View assigned employees</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 cursor-pointer rounded-full text-muted-foreground hover:text-foreground"
                    onClick={() => onConfigure?.(absenceType)}
                    aria-label="Configure"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Configure</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive cursor-pointer"
                    onClick={() => setDeleteDialogOpen(true)}
                    aria-label="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Absence Type</AlertDialogTitle>
            <AlertDialogDescription>
              {hasAssignedEmployees ? (
                <>
                  Cannot delete "{absenceType.name}" because it is assigned to{' '}
                  {absenceType._count!.assignments} employee(s).
                  <span className="mt-2 block text-amber-600 dark:text-amber-400">
                    Deactivate it instead to hide from new assignments while preserving historical data.
                  </span>
                </>
              ) : (
                <>
                  Are you sure you want to delete "{absenceType.name}"? This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteMutation.isError && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
              <p className="font-medium">Failed to delete absence type</p>
              <p className="mt-1 text-xs">
                {(() => {
                  const err = deleteMutation.error;
                  if (err && typeof err === 'object' && 'response' in err) {
                    const axiosErr = err as { response?: { data?: { message?: string } } };
                    if (axiosErr.response?.data?.message) return axiosErr.response.data.message;
                  }
                  return err instanceof Error
                    ? err.message
                    : 'An unexpected error occurred. Please try again.';
                })()}
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleteMutation.isPending}
              onClick={() => {
                setDeleteDialogOpen(false);
                deleteMutation.reset();
              }}
              className="cursor-pointer"
            >
              Cancel
            </AlertDialogCancel>
            {!hasAssignedEmployees && (
              <Button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                variant="destructive"
                className="cursor-pointer"
              >
                {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Employees dialog */}
      <Dialog open={employeesDialogOpen} onOpenChange={setEmployeesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Employees with {absenceType.name}
              {employees && employees.length > 0 && (
                <span className="text-sm font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  {employees.length} {employees.length === 1 ? 'employee' : 'employees'}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              List of employees who have this absence type assigned
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto mt-4">
            {isLoadingEmployees ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3 p-2">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                ))}
              </div>
            ) : employees && employees.length > 0 ? (
              <div className="space-y-2">
                {employees.map((employee) => {
                  const fullName = formatName(employee);
                  const isUnassigning =
                    unassignMutation.isPending && unassignMutation.variables === employee.id;
                  return (
                    <div
                      key={employee.id}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group',
                        isUnassigning && 'opacity-50'
                      )}
                    >
                      <Avatar className="h-10 w-10">
                        {employee.avatarUrl && (
                          <AvatarImage src={employee.avatarUrl} alt={fullName} />
                        )}
                        <AvatarFallback>
                          {getInitials(employee.firstName, employee.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{fullName}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {employee.position?.title && (
                            <span className="truncate">{employee.position.title}</span>
                          )}
                          {employee.department?.name && (
                            <>
                              {employee.position?.title && <span>•</span>}
                              <span className="truncate">{employee.department.name}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive cursor-pointer"
                              onClick={() => unassignMutation.mutate(employee.id)}
                              disabled={unassignMutation.isPending}
                            >
                              {isUnassigning ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Remove from this absence type</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm font-medium text-foreground">No employees assigned</p>
                <p className="text-xs text-muted-foreground mt-1">
                  This absence type is not assigned to any employees yet
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
